import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IonContent,
  IonFooter,
  IonList,
  IonItem,
  IonLabel,
  IonMenu,
  IonMenuToggle,
  IonIcon,
} from '@ionic/angular/standalone';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { addIcons } from 'ionicons';
import {
  alarmOutline,
  homeOutline,
  peopleOutline,
  banOutline,
  peopleCircleOutline,
  flashOutline,
  analyticsOutline,
  chevronUpOutline,
  chevronDownOutline,
  pieChartOutline,
  cashOutline,
  walletOutline,
  cardOutline,
  settingsOutline,
  gitNetworkOutline,
  folderOpenOutline,
  receiptOutline,
  linkOutline,
  personAddOutline,
  documentTextOutline,
  statsChartOutline,
  ticketOutline,
  desktopOutline,
  trophyOutline,
  giftOutline,
  mailOutline,
  notificationsOutline,
  sendOutline,
  listOutline,
  idCardOutline,
  briefcaseOutline,
  mapOutline,
  megaphoneOutline,
  phonePortraitOutline,
  cubeOutline,
} from 'ionicons/icons';
import { isPreReleaseVisible } from '../../../auth/guards/pre-release.guard';
import { AuthUserService } from '../../../services/auth-user.service';
import { PermissionsService } from '../../../services/permissions.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    IonContent,
    IonFooter,
    IonList,
    IonItem,
    IonLabel,
    IonMenu,
    IonMenuToggle,
    IonIcon,
  ],
})
export class SidebarComponent implements OnInit {
  public readonly permissions = inject(PermissionsService);
  private readonly authUser = inject(AuthUserService);

  /**
   * Permisos que abren al menos una pantalla del grupo de Cobranza.
   * Mantener alineado con los guards de las rutas `collections/*` en app.routes.ts.
   */
  private static readonly COBRANZA_PERMISSIONS = [
    'view_collection_processing',
    'view_direct_debit',
    'view_invoices',
    'view_agent_assignments',
    'view_pending_tasks',
    'approve_payment_receipts',
    'view_payment_receipts',
    'view_collection_orders',
    'view_collection_invoicing',
  ];

  /**
   * Permisos que abren al menos una pantalla del grupo de Gráficas.
   * `view_charts` es la general; `view_invoices`, el dashboard de Facturación.
   */
  private static readonly CHARTS_PERMISSIONS = ['view_charts', 'view_invoices'];

  /** Permisos de las pantallas del grupo de Comunicación (mailing y Comunicación interna). */
  private static readonly COMUNICACION_PERMISSIONS = [
    'view_mailings',
    'manage_internal_notifications',
  ];

  /**
   * Los 7 permisos de `asset_management` (Puntos Terra) — cualquiera de ellos
   * abre al menos una sección de `LoyaltyAdminPage`. Mantener alineado con
   * `permissionGuard([...])` de la ruta `asset-management/loyalty` en
   * app.routes.ts.
   */
  private static readonly ASSET_MANAGEMENT_PERMISSIONS = [
    'view_loyalty_catalog',
    'manage_loyalty_catalog',
    'view_loyalty_balances',
    'adjust_loyalty_points',
    'view_loyalty_redemptions',
    'resolve_loyalty_redemptions',
    'review_loyalty_receipts',
  ];

  /**
   * Permisos de las pantallas del grupo de Recursos Humanos: Organigrama
   * Empresarial (`view_org_chart`, migración 062) y Expedientes
   * (`view_employee_files`, migración 097). Mantener alineado con los guards de
   * `/rh/organigrama` y `/rh/expedientes` en app.routes.ts.
   */
  private static readonly RH_PERMISSIONS = ['view_org_chart', 'view_employee_files'];

  /** Permisos de las pantallas del grupo de Supply Chain: Indicadores, Artículos y Disponibilidad. */
  private static readonly SUPPLY_CHAIN_PERMISSIONS = [
    'view_inventory_catalog',
    'view_panel_availability',
    'view_supply_chain_analytics',
  ];

  /** Permisos de las pantallas del grupo de Ventas (`view_sales_indicators` llega en la fase 11). */
  private static readonly VENTAS_PERMISSIONS = ['view_quotes', 'view_coverage'];

  /** Permisos de las pantallas del grupo de Marketing. */
  private static readonly MARKETING_PERMISSIONS = [
    'view_leads',
    'view_lead_email_blocklist',
    'view_lead_acquisition_costs',
    'view_lead_acquisition_analytics',
    'manage_lead_referrals',
  ];

  /**
   * Permisos de las pantallas del grupo App Clientes (clientes.terraenergy.mx).
   * Es un grupo propio, no un sub-ítem de Clientes, porque va a crecer con
   * más módulos de la app. Hoy solo el listado de usuarios
   * (`view_customer_app_accounts`, migración 074). Mantener alineado con los
   * guards de `/customer-app/*` en app.routes.ts.
   */
  private static readonly APP_CLIENTES_PERMISSIONS = ['view_customer_app_accounts'];

  /** El grupo de Cobranza se muestra solo si el usuario alcanza alguna de sus pantallas. */
  readonly showCobranzaMenu = computed(() =>
    SidebarComponent.COBRANZA_PERMISSIONS.some((slug) => this.permissions.hasPermission(slug)),
  );

  /** El grupo de Gráficas se muestra solo si el usuario alcanza alguna de sus pantallas. */
  readonly showChartsMenu = computed(() =>
    SidebarComponent.CHARTS_PERMISSIONS.some((slug) => this.permissions.hasPermission(slug)),
  );

  /** El grupo de Asset Management se muestra solo si el usuario alcanza alguna sección de Puntos Terra. */
  readonly showAssetManagementMenu = computed(() =>
    SidebarComponent.ASSET_MANAGEMENT_PERMISSIONS.some((slug) =>
      this.permissions.hasPermission(slug),
    ),
  );

  /**
   * El grupo de Comunicación se muestra si el usuario alcanza alguna de sus
   * pantallas: el mailing a clientes con `view_mailings` (campañas, listas y
   * plantillas; enviar, escribir plantillas o administrar listas piden permisos
   * aparte dentro de cada una) o Comunicación interna con
   * `manage_internal_notifications`, que es solo de T.I. Cada entrada se gatea
   * por su cuenta en el template.
   */
  readonly showComunicacionMenu = computed(() =>
    SidebarComponent.COMUNICACION_PERMISSIONS.some((slug) => this.permissions.hasPermission(slug)),
  );

  /** El grupo de Marketing se muestra fuera de producción si el usuario alcanza alguna de sus pantallas. */
  readonly showMarketingMenu = computed(
    () =>
      isPreReleaseVisible() &&
      SidebarComponent.MARKETING_PERMISSIONS.some((slug) => this.permissions.hasPermission(slug)),
  );

  /** El grupo de Ventas se muestra fuera de producción si el usuario alcanza alguna de sus pantallas. */
  readonly showVentasMenu = computed(
    () =>
      isPreReleaseVisible() &&
      SidebarComponent.VENTAS_PERMISSIONS.some((slug) => this.permissions.hasPermission(slug)),
  );

  /** El grupo de Recursos Humanos se muestra solo si el usuario alcanza alguna de sus pantallas. */
  readonly showRhMenu = computed(() =>
    SidebarComponent.RH_PERMISSIONS.some((slug) => this.permissions.hasPermission(slug)),
  );

  /** El grupo de Supply Chain se muestra fuera de producción si el usuario alcanza alguna de sus pantallas. */
  readonly showSupplyChainMenu = computed(
    () =>
      isPreReleaseVisible() &&
      SidebarComponent.SUPPLY_CHAIN_PERMISSIONS.some((slug) =>
        this.permissions.hasPermission(slug),
      ),
  );

  /** El grupo App Clientes se muestra solo si el usuario alcanza alguna de sus pantallas. */
  readonly showAppClientesMenu = computed(() =>
    SidebarComponent.APP_CLIENTES_PERMISSIONS.some((slug) => this.permissions.hasPermission(slug)),
  );

  /** Signal para controlar el estado expandido/colapsado del menú de gráficas */
  chartsMenuExpanded = signal(false);

  /** Signal para controlar el estado expandido/colapsado del menú de cobranza */
  cobranzaMenuExpanded = signal(false);

  /** Signal para controlar el estado expandido/colapsado del menú de Clientes */
  clientesMenuExpanded = signal(false);

  /** Signal para controlar el estado expandido/colapsado del menú de Tickets */
  ticketsMenuExpanded = signal(false);

  /** Signal para controlar el estado expandido/colapsado del menú de Asset Management */
  assetManagementMenuExpanded = signal(false);

  /** Signal para controlar el estado expandido/colapsado del menú de Comunicación */
  comunicacionMenuExpanded = signal(false);

  /** Signal para controlar el estado expandido/colapsado del menú de Recursos Humanos */
  rhMenuExpanded = signal(false);

  /** Signal para controlar el estado expandido/colapsado del menú de Supply Chain */
  supplyChainMenuExpanded = signal(false);

  /** Signal para controlar el estado expandido/colapsado del menú de Ventas */
  ventasMenuExpanded = signal(false);

  /** Signal para controlar el estado expandido/colapsado del menú de Marketing */
  marketingMenuExpanded = signal(false);

  /** Signal para controlar el estado expandido/colapsado del menú de App Clientes */
  appClientesMenuExpanded = signal(false);

  constructor() {
    addIcons({
      phonePortraitOutline,
      alarmOutline,
      homeOutline,
      peopleOutline,
      banOutline,
      peopleCircleOutline,
      flashOutline,
      analyticsOutline,
      chevronUpOutline,
      chevronDownOutline,
      pieChartOutline,
      cashOutline,
      walletOutline,
      cardOutline,
      settingsOutline,
      gitNetworkOutline,
      folderOpenOutline,
      receiptOutline,
      linkOutline,
      personAddOutline,
      documentTextOutline,
      statsChartOutline,
      ticketOutline,
      desktopOutline,
      trophyOutline,
      giftOutline,
      mailOutline,
      notificationsOutline,
      sendOutline,
      listOutline,
      idCardOutline,
      briefcaseOutline,
      mapOutline,
      megaphoneOutline,
      cubeOutline,
    });
  }

  /**
   * Carga usuario y permisos. Es indispensable hacerlo aquí: el sidebar se
   * renderiza antes que la página y `hasPermission` es síncrono, así que sin
   * esto el menú se pintaría vacío hasta que alguna página cargara los permisos.
   * Ambos métodos son idempotentes (mismo patrón que `permissionGuard`).
   */
  async ngOnInit(): Promise<void> {
    await this.authUser.loadCurrentUser();
    await this.permissions.loadPermissions();
  }

  /** Alterna la visibilidad del menú de gráficas */
  toggleChartsMenu(): void {
    this.chartsMenuExpanded.update((v) => !v);
  }

  /** Alterna la visibilidad del menú de cobranza */
  toggleCobranzaMenu(): void {
    this.cobranzaMenuExpanded.update((v) => !v);
  }

  /** Alterna la visibilidad del menú de tickets */
  toggleTicketsMenu(): void {
    this.ticketsMenuExpanded.update((v) => !v);
  }

  /** Alterna la visibilidad del menú de Comunicación */
  toggleComunicacionMenu(): void {
    this.comunicacionMenuExpanded.update((v) => !v);
  }

  /** Alterna la visibilidad del menú de Clientes */
  toggleClientesMenu(): void {
    this.clientesMenuExpanded.update((v) => !v);
  }

  /** Alterna la visibilidad del menú de Asset Management */
  toggleAssetManagementMenu(): void {
    this.assetManagementMenuExpanded.update((v) => !v);
  }

  /** Alterna la visibilidad del menú de Recursos Humanos */
  toggleRhMenu(): void {
    this.rhMenuExpanded.update((v) => !v);
  }

  /** Alterna la visibilidad del menú de Supply Chain */
  toggleSupplyChainMenu(): void {
    this.supplyChainMenuExpanded.update((v) => !v);
  }

  /** Alterna la visibilidad del menú de Ventas */
  toggleVentasMenu(): void {
    this.ventasMenuExpanded.update((v) => !v);
  }

  /** Alterna la visibilidad del menú de Marketing */
  toggleMarketingMenu(): void {
    this.marketingMenuExpanded.update((v) => !v);
  }

  /** Alterna el estado del menú de App Clientes */
  toggleAppClientesMenu(): void {
    this.appClientesMenuExpanded.update((v) => !v);
  }
}
